import { useContext, useEffect, useState } from 'react'
import { NextRouter, useRouter } from 'next/router'
import { ParsedUrlQueryInput } from 'querystring'
import styled from 'styled-components'

import { Brand } from '@awazevr/design-system.components.awaze-theme-provider'
import {
  BaseGrid,
  BaseGridItem,
} from '@awazevr/design-system.components.base-grid'
import { Box } from '@awazevr/design-system.components.box'
import { Button } from '@awazevr/design-system.components.button'
import { Container } from '@awazevr/design-system.components.container'
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
} from '@awazevr/design-system.components.drawer'
import NoResults from '@awazevr/design-system.compositions.no-results'
import {
  SearchFormConfig,
  SearchFormFields,
} from '@awazevr/design-system.compositions.search-form'
import {
  Toaster,
  useToaster,
} from '@awazevr/design-system.compositions.toaster'
import { usePrevious, useWindowWidth } from '@awazevr/design-system.utils.react'
import { readSmartSearchPrompt } from '@awazevr/mfe-common.compositions.search-experience'
import { useGuestAuth } from '@awazevr/mfe-common.contexts.guest-auth'
import { useIntlContext } from '@awazevr/mfe-common.contexts.intl'
import {
  FILTER_UPDATE_NIGHTS,
  FILTER_UPDATE_RANGE,
  getResults,
  NO_RESULTS,
  RESULTS_UPDATED,
  RESULTS_UPDATING,
  ResultsUpdatedProps,
  SearchContextStateType,
  useSearchContextDispatch,
  useSearchContextState,
} from '@awazevr/mfe-common.contexts.search'
import {
  DataLayerEventType,
  DataLayerFactory,
  isTmsReady,
  TmsContext,
  TmsContextType,
} from '@awazevr/mfe-common.contexts.tms'
import {
  WishlistProvider,
  WishlistTrackEventType,
} from '@awazevr/mfe-common.contexts.wishlist'
import { OptimizelyDecisionWithOutUserContext } from '@awazevr/mfe-common.lib.optimizely'
import { Regions } from '@awazevr/mfe-common.types-and-mocks.one-search-client'
import type {
  CommonSearchResult,
  CommonSite,
  ICommonAggregations,
  SearchCriteria,
  SearchFormCriteria,
} from '@awazevr/mfe-common.types-and-mocks.site-search-service'

import { useDataLayerEvent } from '@/components/SearchForm/hooks/useDataLayerEvent'
import {
  getIsTablet,
  onDesktop,
  onMobile,
  onTablet,
} from '@/components/utils/dimension'
import {
  CORRELATION_ID_COOKIE_NAME,
  OPTIMIZELY_EVENTS,
  OPTIMIZELY_FLAGS_ENUM,
} from '@/constants'
import Filters from '@/containers/Filters'
import SearchResults from '@/containers/SearchResults'
import { useOptimizelyDecisionsContext } from '@/context/optimizely-decisions-context'
import {
  ExplorationSuggestions,
  resolveExplorationVariant,
} from '@/experiment/AS_2696_Exploration_Suggestions'
import {
  buildPropertiesNearbyHandlers,
  buildViewMoreHref,
  isExpandedCoordinatesSearch,
  LowResultsLink,
  openDatesOverlay,
  outsideDestinationSubtext,
  PropertiesNearby,
  shouldShowPropertiesNearby,
  trackChangeDatesFlexClick,
  trackNoResultsDurationClick,
  trackNoResultsFlexibilityClick,
  trackSmartPolygonFullListSeen,
  trackViewMoreClick,
  usePropertyCardMapper,
} from '@/experiment/AS_2729_Properties_Nearby'
import { SearchExperienceSwitch } from '@/experiment/SEARCH_BETA_SPIKE/SearchExperienceSwitch/SearchExperienceSwitch'
import { useApplicationConfig } from '@/lib/applicationConfig'
import { useDisplayContext } from '@/lib/display/displayContext'
import { SearchResultsTranslations } from '@/types'
import { dataLayerErrorHandler } from '@/utils/dataLayerErrorHandler'
import { getCookie, searchCriteriaComparisonString } from '@/utils/helpers'
import { trackClientSideOptimizelyEvent } from '@/utils/optimizely/trackOptimizelyEvent'

import {
  createSearchNoResultsPayload,
  createSearchPageViewPayload,
  onWishlistTrack,
  wishlistTmsUpdateDataLayer,
} from './SearchLayout.utils'

export interface SearchLayoutProps {
  brand: Brand
  isSwitchingExperience?: boolean
  onTryBetaSearch?: () => void
  filterControls: {
    open: () => void
    close: () => void
    isOpen: boolean
  }
  defaultConfig: SearchFormConfig
  initialValues: SearchFormFields
  translations: SearchResultsTranslations
  experimentDecisions?: Record<string, OptimizelyDecisionWithOutUserContext>
  optimizelyUserId: string
  host: string
}

const ShowOnDesktop = styled(Container)`
  display: none;

  ${onDesktop} {
    display: initial;
  }
`

const HideOnDesktop = styled(Container)`
  display: initial;

  ${onDesktop} {
    display: none;
  }
`

// Centred flex (like the results page) so the widget's mobile width:100vw
// bleeds to both edges; padding/negative margin tune the gap to the message.
const NoResultsExploration = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 1rem;
  margin-bottom: -2rem;

  ${onTablet} {
    padding-top: 1.5rem;
  }
`

const NearbyNoResults = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  text-align: center;
  padding: 40px 1rem 8px;
`

const NearbyNoResultsHeading = styled.h2`
  font-family: Poppins;
  font-size: 20px;
  font-weight: ${({ theme }) => theme.FontHeadingHeading300FontWeight};
  line-height: 28px;
  color: ${({ theme }) => theme.ColorTextBodyOnLight};
  margin: 0;
`

const NearbyNoResultsSubtext = styled.p`
  font-size: 16px;
  color: ${({ theme }) => theme.ColorTextBodyOnLight};
  margin: 0;
`

// On the no-results page the carousel is capped and centred (per the design);
// below 840px it stays full width so the mobile viewport breakout is unaffected.
const PropertiesNearbyNoResultsWrap = styled.div`
  width: 100%;
  max-width: 840px;
  margin: 0 auto;
`

// Mobile only: clips the scrollbar-width overhang of the carousel's 100vw bleed
// so it can't add a whole-page horizontal scroll. Scoped to mobile so it doesn't
// clip card/button shadows at the container edge on desktop (clip, not hidden,
// to keep sticky/scroll).
const PageContainer = styled(Container)`
  ${onMobile} {
    overflow-x: clip;
  }
`

const NoResultsExperienceSwitch = styled.div`
  display: flex;
  justify-content: flex-start;
  padding: 0 16px 4px;

  ${onDesktop} {
    justify-content: flex-end;
    padding-inline: 0;
  }
`

export const SearchLayout: React.FC<SearchLayoutProps> = ({
  brand,
  isSwitchingExperience = false,
  onTryBetaSearch,
  filterControls: { open, close, isOpen },
  defaultConfig,
  initialValues,
  translations,
  experimentDecisions = {},
  optimizelyUserId,
  host,
}) => {
  const {
    total = 0,
    hasResults,
    loading,
    searchCriteria = {},
    aggregations,
    rhsLevel,
  } = useSearchContextState()
  const dispatch = useSearchContextDispatch()
  const searchContextState = useSearchContextState()
  const { displayMode } = useDisplayContext()
  const { clientHeader, contentfulLocale, currency } = useApplicationConfig()
  const { formatMessage } = useIntlContext()
  const router = useRouter()
  const tms = useContext(TmsContext)
  const width = useWindowWidth()
  const isTablet = width ? getIsTablet(width) : false
  const optiContext = useOptimizelyDecisionsContext()

  // AS-2729: expandedProperties is dropped by the search reducer's RESULTS_UPDATED
  // action, so we capture it here from the raw response and thread it downwards.
  const [expandedProperties, setExpandedProperties] = useState<CommonSite[]>([])

  const isExpandedSearch = isExpandedCoordinatesSearch(
    (searchCriteria as { useExpandedCoordinates?: boolean | string })
      .useExpandedCoordinates,
  )
  const isDatedSearch = Boolean(searchCriteria.start)
  const mapToPropertyCards = usePropertyCardMapper(searchCriteria)

  const previousState = usePrevious(searchContextState)

  const parsedSearchCriteria = (
    stateSearchCriteria: SearchCriteria,
  ): ParsedUrlQueryInput => {
    const features = stateSearchCriteria.features?.join(',')
    const siteFeatures = stateSearchCriteria.siteFeatures?.join(',')
    const minPrice = stateSearchCriteria.minPrice
    const maxPrice = stateSearchCriteria.maxPrice

    return {
      ...stateSearchCriteria,
      features: features !== '' ? features : [],
      siteFeatures: siteFeatures !== '' ? siteFeatures : [],
      bathrooms: stateSearchCriteria.bathrooms
        ? stateSearchCriteria.bathrooms
        : [],
      bedrooms: stateSearchCriteria.bedrooms
        ? stateSearchCriteria.bedrooms
        : [],
      grade: stateSearchCriteria.grade ? stateSearchCriteria.grade : [],
      rating: stateSearchCriteria.rating ? stateSearchCriteria.rating : [],
      ...(minPrice && { minPrice }),
      ...(maxPrice && { maxPrice }),
    }
  }

  const updateQueryParam = (
    router: NextRouter,
    stateSearchCriteria: SearchFormCriteria,
  ): void => {
    // AGE-199 - nextjs catch-all routes adds path params to query params
    // e.g. /england/ will add '?index=england' to the URL
    // this is undesirable for us, so we remove those params

    const query = parsedSearchCriteria(stateSearchCriteria)

    const sanitisedParams: ParsedUrlQueryInput = {
      ...query,
    }

    delete sanitisedParams.index
    delete sanitisedParams.regionUrl

    if (sanitisedParams.minPrice === null) delete sanitisedParams.minPrice
    if (sanitisedParams.maxPrice === null) delete sanitisedParams.maxPrice

    router.replace(
      {
        pathname: window.location.pathname,
        query: sanitisedParams,
      },
      undefined,
      { shallow: true },
    )
  }

  const fireSearchNoResultsDataLayerEvent = (
    criteria: SearchCriteria,
    tms: TmsContextType,
  ): void => {
    const searchNoResultsPayload = createSearchNoResultsPayload(
      criteria,
      readSmartSearchPrompt(brand),
    )

    new DataLayerFactory()
      .getEvent(DataLayerEventType.SearchNoResults)
      .update(tms, searchNoResultsPayload, dataLayerErrorHandler)
  }

  const fireDataLayerEvents = (
    state: SearchContextStateType,
    searchCriteria: SearchCriteria,
    tms: TmsContextType,
    searchResults: CommonSearchResult,
  ): void => {
    if (searchResults.properties.length === 0) {
      fireSearchNoResultsDataLayerEvent(searchCriteria, tms)
      return
    }

    const stateSearchCriteria = state.searchCriteria ?? {}
    const getPreviousPage = (
      previousState: SearchContextStateType | undefined,
    ): number => {
      return (
        previousState?.searchCriteria?.page ||
        getCurrentPage(stateSearchCriteria)
      )
    }

    const getCurrentPage = (searchCriteriaState: SearchCriteria): number => {
      return searchCriteriaState.page || 1
    }

    const isFullSmartPolygonSearch = isExpandedCoordinatesSearch(
      (
        searchCriteria as {
          useExpandedCoordinates?: boolean | string
        }
      ).useExpandedCoordinates,
    )

    if (isFullSmartPolygonSearch) {
      trackSmartPolygonFullListSeen(tms, searchCriteria, searchResults)
    } else {
      new DataLayerFactory()
        .getEvent(DataLayerEventType.SearchPageView)
        .update(
          tms,
          createSearchPageViewPayload(
            stateSearchCriteria,
            searchResults,
            readSmartSearchPrompt(brand),
          ),
          dataLayerErrorHandler,
        )
    }

    const currentPage = getCurrentPage(stateSearchCriteria)
    const previousPage = getPreviousPage(previousState)

    if (searchResults?.properties?.length && previousPage !== currentPage) {
      new DataLayerFactory()
        .getEvent(DataLayerEventType.PaginationClick)
        .update(tms, { pageNumber: currentPage }, dataLayerErrorHandler)
    }
  }

  const searchCriteriaUpdated = searchCriteriaComparisonString(searchCriteria)

  const tmsReady = isTmsReady(tms)

  useEffect(() => {
    const shouldTriggerDataLayerPush =
      tmsReady && Object.keys(experimentDecisions).length > 0

    typeof newrelic !== 'undefined' &&
      tmsReady &&
      Object.keys(experimentDecisions).length === 0 &&
      newrelic.addPageAction('Optimizely-LogDecisions-NoDecisions', {
        userId: optimizelyUserId,
      })

    if (shouldTriggerDataLayerPush) {
      typeof newrelic !== 'undefined' &&
        newrelic.addPageAction('Optimizely-LogDecisions-Trigger', {
          userId: optimizelyUserId,
        })

      Object.keys(experimentDecisions).forEach((experimentDecision) => {
        new DataLayerFactory()
          .getEvent(DataLayerEventType.ExperienceImpression)
          .update(
            tms,
            {
              experiment_origin: 'flag',
              experiment_decision: experimentDecisions[experimentDecision],
            },
            dataLayerErrorHandler,
          )
      })
    }
  }, [tmsReady])

  // Once per page load, not per results update.
  useEffect(() => {
    void trackClientSideOptimizelyEvent(
      OPTIMIZELY_EVENTS.PAGE_VIEW,
      optiContext,
    )
  }, [])

  // When the search criteria or display mode changes.
  useEffect(() => {
    dispatch({ type: RESULTS_UPDATING })
    // Clear before each request so a failure (or a response without
    // expandedProperties) can't leave the previous search's cards behind.
    setExpandedProperties([])
    const isMapMode = displayMode === 'MAP'
    const mapEndpoint = '/api/search/properties/coordinates'
    const listEndpoint = '/api/search/properties/list'

    const endpoint = isMapMode ? mapEndpoint : listEndpoint
    // Don't request the expanded set in map mode or once already on the
    // expanded page.
    const shouldRequestExpanded = !isMapMode && !isExpandedSearch
    const additionalParams: Record<string, string> = shouldRequestExpanded
      ? { showExpandedResults: 'true' }
      : {}

    if (
      optiContext.getExperimentVariationKey(
        OPTIMIZELY_FLAGS_ENUM.ONESEARCH_SINGLE_CARDINALITY_BLENDED,
      ) === 'variant_a'
    ) {
      additionalParams.useCombinedCottagesAndNovasolGroups = 'true'
    }

    getResults(searchCriteria, brand, endpoint, additionalParams, {
      headers: {
        'x-awaze-client': clientHeader,
        'x-awaze-currency': currency,
        'x-awaze-locale': contentfulLocale,
      },
    })
      .then((results) => {
        const resultsWithProperties = 'properties' in results
        const resultsWithCoordinates = 'coordinates' in results
        const filteredCoordinates = resultsWithCoordinates
          ? results.coordinates.filter(
              ({ lat, lng }) => lat !== -999 && lng !== -999,
            )
          : []

        if (resultsWithProperties) {
          fireDataLayerEvents(searchContextState, searchCriteria, tms, results)
        }

        // AS-2729: capture expandedProperties before the reducer drops it.
        setExpandedProperties(
          'expandedProperties' in results &&
            Array.isArray(results.expandedProperties)
            ? results.expandedProperties
            : [],
        )

        const completeResults: ResultsUpdatedProps = {
          coordinates: [],
          properties: [],
          rhsLevel: 'rhs1', // HACK: We do this also in Filters.tsx
          ...results,
          ...(resultsWithCoordinates && {
            coordinates: filteredCoordinates,
          }),
        }

        dispatch({
          type: RESULTS_UPDATED,
          results: completeResults,
        })
      })
      .catch((error) => {
        if (typeof newrelic !== 'undefined') {
          const correlationId = getCookie(CORRELATION_ID_COOKIE_NAME)
          newrelic.noticeError(
            error,
            correlationId ? { correlationId } : undefined,
          )
        }

        fireSearchNoResultsDataLayerEvent(searchCriteria, tms)

        dispatch({
          type: NO_RESULTS,
        })
      })
      .finally(() => {
        updateQueryParam(router, searchCriteria)
      })
  }, [searchCriteriaUpdated, displayMode])

  useDataLayerEvent({ isOpen: isOpen, tms: tms })

  const showNoResults = !hasResults && !loading && displayMode === 'LIST'

  if (showNoResults) {
    trackClientSideOptimizelyEvent(OPTIMIZELY_EVENTS.NO_RESULTS, optiContext)
  }

  // SearchResults isn't rendered on no-results, so resolve the widget here too
  // (AS-2696) — only when needed, to avoid duplicating the work on results pages.
  const explorationVariant = showNoResults
    ? resolveExplorationVariant(searchCriteria.placesId, optiContext)
    : null

  const showPropertiesNearby = shouldShowPropertiesNearby({
    isExpandedSearch,
    expandedPropertiesCount: expandedProperties.length,
  })
  const expandedPropertyCards = showPropertiesNearby
    ? mapToPropertyCards(expandedProperties)
    : []

  const handlePropertiesNearbyViewMore = (): void => {
    trackViewMoreClick(tms)
    // Full page load (not router.push) so the search re-runs server-side from
    // the new URL — the mounted search-context reducer ignores prop changes.
    window.location.assign(buildViewMoreHref(searchCriteria))
  }
  const {
    onCardClick: handlePropertiesNearbyCardClick,
    onSeen: handlePropertiesNearbySeen,
  } = buildPropertiesNearbyHandlers({
    tms,
    searchCriteria,
    expandedProperties,
    optimizelyState: optiContext,
  })

  const changeDatesFlexLabel = formatMessage({
    id: 'search-results.no-results.change-dates-flexibility',
    defaultMessage: 'Try changing your dates and flexibility',
  })

  const nearbyNoResultsHeading = formatMessage({
    id: 'search-results.no-results.expanded-heading',
    defaultMessage: "We don't have any available properties for your search",
  })

  const nearbyNoResultsSubtext = formatMessage(outsideDestinationSubtext, {
    destination:
      searchCriteria.regionName ||
      formatMessage({
        id: 'search-results.no-results.your-chosen-area',
        defaultMessage: 'your chosen area',
      }),
  })

  const noResultsMessage = formatMessage({
    id: 'search-results.no-results.message',
    defaultMessage:
      'Unfortunately there were no results for your search, please try selecting a different date or duration.',
  })

  const firstActionLabel = formatMessage(
    {
      id: 'search-results.no-results.change-duration',
      defaultMessage: 'Change duration to 14 nights',
    },
    {
      nights: 14,
    },
  )

  const { add } = useToaster()

  const onWishlistError = (
    error: unknown,
    context: string,
  ): void | undefined => {
    if (typeof newrelic !== 'undefined') {
      newrelic.noticeError(error as Error, { context })
    }
  }
  return (
    <PageContainer>
      {showNoResults && onTryBetaSearch && (
        <NoResultsExperienceSwitch>
          <SearchExperienceSwitch
            placement="traditional-standalone"
            busy={isSwitchingExperience}
            onClick={onTryBetaSearch}
          />
        </NoResultsExperienceSwitch>
      )}
      {showNoResults ? (
        <BaseGrid>
          <BaseGridItem columns={12}>
            {/* Nearby message above the carousel; only when expanded
                properties are returned. */}
            {showPropertiesNearby && (
              <NearbyNoResults>
                <NearbyNoResultsHeading>
                  {nearbyNoResultsHeading}
                </NearbyNoResultsHeading>
                <NearbyNoResultsSubtext>
                  {nearbyNoResultsSubtext}
                </NearbyNoResultsSubtext>
                {/* Undated searches hide the dates/flexibility CTA per the visuals. */}
                {isDatedSearch && (
                  <LowResultsLink
                    type="button"
                    onClick={() => {
                      trackChangeDatesFlexClick(tms)
                      openDatesOverlay()
                    }}
                  >
                    {changeDatesFlexLabel}
                  </LowResultsLink>
                )}
              </NearbyNoResults>
            )}
            {showPropertiesNearby && (
              <PropertiesNearbyNoResultsWrap>
                <PropertiesNearby
                  properties={expandedPropertyCards}
                  onViewMore={handlePropertiesNearbyViewMore}
                  onCardClick={handlePropertiesNearbyCardClick}
                  onSeen={handlePropertiesNearbySeen}
                />
              </PropertiesNearbyNoResultsWrap>
            )}
            {explorationVariant && (
              <NoResultsExploration>
                <ExplorationSuggestions
                  searchedPlace={explorationVariant.searchedPlace}
                  tiles={explorationVariant.tiles}
                  searchCriteria={searchCriteria}
                  optimizelyState={optiContext}
                />
              </NoResultsExploration>
            )}
            {/* Standard no-results view when no expanded properties return. */}
            {!showPropertiesNearby && (
              <Box
                radius={0}
                padding={[
                  [60, 0],
                  [60, 0],
                  [60, 0],
                ]}
              >
                <NoResults
                  onFirstActionClick={() => {
                    trackNoResultsDurationClick(tms)
                    dispatch({ type: FILTER_UPDATE_NIGHTS, nights: 14 })
                  }}
                  onSecondActionClick={() => {
                    trackNoResultsFlexibilityClick(tms)
                    dispatch({ type: FILTER_UPDATE_RANGE, range: 3 })
                  }}
                  translations={{
                    noResultsMessage: noResultsMessage,
                    actionsHeading: formatMessage({
                      id: 'search-results.no-results.action-heading',
                      defaultMessage: 'Get more results',
                    }),
                    firstActionLabel: firstActionLabel,
                    secondActionLabel: formatMessage(
                      {
                        id: 'search-results.no-results.change-flexibility',
                        defaultMessage: 'Change flexibility to +/-3 days',
                      },
                      {
                        range: 3,
                      },
                    ),
                  }}
                />
              </Box>
            )}
          </BaseGridItem>
        </BaseGrid>
      ) : (
        <BaseGrid flush={[false, false, true]}>
          <BaseGridItem columns={[0, 0, 3]}>
            <ShowOnDesktop>
              <Filters />
            </ShowOnDesktop>
          </BaseGridItem>

          <BaseGridItem columns={[12, 12, isTablet ? 12 : 9]}>
            {/* IMPORTANT Provider scope - See ADR: adr/[2026-01-12]-grapql-providers.md */}
            <WishlistProvider
              onToast={(toast) => {
                add(toast as unknown as Parameters<typeof add>[0])
              }}
              onError={onWishlistError}
              onTrack={(
                event: WishlistTrackEventType,
                payload?: Record<string, unknown>,
              ) => {
                if (
                  event === 'wishlist_undo_remove' ||
                  event === 'wishlist_add_failed' ||
                  event === 'wishlist_remove_failed'
                ) {
                  wishlistTmsUpdateDataLayer(tms, event, payload)
                  return
                }

                payload?.propertyId &&
                  onWishlistTrack({
                    tms,
                    eventName: event,
                    clientHeader,
                    currency,
                    contentfulLocale,
                    propertyInfoArgs: {
                      brand,
                      propertyId: payload?.propertyId.toString(),
                      searchCriteria,
                      ...(searchCriteria.salesMarket && {
                        salesMarket: searchCriteria.salesMarket,
                      }),
                    },
                    propertyImpression: {
                      aggregations: aggregations as ICommonAggregations,
                      rhsLevel: rhsLevel as Regions,
                      total,
                    },
                  })
              }}
              useGuestAuth={useGuestAuth}
              useIntlContext={useIntlContext}
              hostname={host}
              silentQueryErrors
            >
              <SearchResults
                isSwitchingExperience={isSwitchingExperience}
                onTryBetaSearch={onTryBetaSearch}
                openFilters={open}
                defaultConfig={defaultConfig}
                initialValues={initialValues}
                translations={translations}
                isFilterDrawerOpen={isOpen}
                expandedProperties={expandedProperties}
              />
            </WishlistProvider>
            <Toaster position="bottom-center" />
            <HideOnDesktop>
              <Drawer isOpen={isOpen} placement="left" close={close}>
                <DrawerContent
                  header={formatMessage({
                    id: 'search-layout.filters',
                    defaultMessage: 'Filters',
                  })}
                  closeButtonTitle={formatMessage({
                    id: 'search-layout.close-filters.ariaLabel',
                    defaultMessage: 'Close filters',
                  })}
                >
                  <DrawerBody>
                    <Filters />
                  </DrawerBody>

                  <DrawerFooter>
                    <Button
                      text={
                        total > 1
                          ? formatMessage(
                              {
                                id: 'search-layout.show-results',
                                defaultMessage: 'Show {total} properties',
                              },
                              {
                                ...(!hasResults && displayMode === 'MAP'
                                  ? { total: 0 }
                                  : { total }),
                              },
                            )
                          : formatMessage({
                              id: 'search-layout.show-result',
                              defaultMessage: 'Show 1 property',
                            })
                      }
                      onClick={close}
                      colourScheme="brand-primary"
                      isFullWidth
                    />
                  </DrawerFooter>
                </DrawerContent>
              </Drawer>
            </HideOnDesktop>
          </BaseGridItem>
        </BaseGrid>
      )}
    </PageContainer>
  )
}
